import _ from 'lodash'
import { createSelector } from 'reselect'

import { paramSelector } from '../../utils/selectors'
import { RootState } from '../types'
import { Dictionary } from '../../utils/types'

import { generateAddToCartLoadingId, generateRemoveOrderItemLoadingId } from './utils'

type WithCustomerRequestId = {
  customerRequestId: string
}

const selectState = (state: RootState) => state.order

const selectConfig = createSelector(
  (state: RootState) => state.config,
  c => c
)

export const getOrderId = createSelector(selectState, x => x.orderMetadata?.id)

export const isOrderLoading = createSelector(selectState, state => state.loading)

export const isMetaDataLoading = createSelector(selectState, state => state.loadingEntities['metaData'])

export const getOrderItems = createSelector(selectState, state => state.orderItems)

export const isRemovingOrderItem = createSelector(
  selectState,
  state =>
    (state.loadingEntities &&
      Object.keys(state.loadingEntities).some(k => k.startsWith('remove')) &&
      Object.values(state.loadingEntities).includes(true)) ||
    false
)

export const isLoadingOrderItem = createSelector(
  selectState,
  paramSelector<WithCustomerRequestId>(),
  (state, { customerRequestId }) =>
    (state.loadingEntities && state.loadingEntities[generateRemoveOrderItemLoadingId(customerRequestId)]) || false
)

export const isLoadingOrderItems = createSelector(
  selectState,
  paramSelector<{ customerRequestIds: string[] }>(),
  (state, { customerRequestIds }): Dictionary<boolean> =>
    (state.loadingEntities &&
      _.reduce(
        customerRequestIds,
        (prev, curr) => ({ ...prev, [curr]: state.loadingEntities[generateRemoveOrderItemLoadingId(curr)] ?? false }),
        {}
      )) ||
    {}
)

export const getNumberOfOrderItems = createSelector(selectState, state => state.orderMetadata?.itemCount || 0)

export const isAccountingError = createSelector(selectState, state => state.orderData?.isAccountingError)

export const isAnyCustomerRequestError = createSelector(
  selectState,
  state => state.orderData?.isAnyCustomerRequestError
)

export const getOrderError = createSelector(selectState, state => state.orderError)

export const getOrderPrices = createSelector(selectState, x => ({
  totalValueWithTaxFormatted: x.orderData?.totalValueWithTaxFormatted,
  totalNetValueFormatted: x.orderData?.totalNetValueFormatted
}))

export const isAddingToCartOrBuyingNow = createSelector(
  selectState,
  state =>
    (state.loadingEntities &&
      Object.keys(state.loadingEntities).some(k => k.startsWith('addToCart') || k.startsWith('buyNow')) &&
      Object.values(state.loadingEntities).includes(true)) ||
    false
)

export const isReportAddingToCart = createSelector(
  selectState,
  paramSelector<{ companyId?: string; reportCode: string }>(),
  (state, { companyId, reportCode }) =>
    (state.loadingEntities && state.loadingEntities[generateAddToCartLoadingId(companyId, reportCode, 'addToCart')]) ||
    false
)

export const isReportBuyingNow = createSelector(
  selectState,
  paramSelector<{ companyId?: string; reportCode: string }>(),
  (state, { companyId, reportCode }) =>
    (state.loadingEntities && state.loadingEntities[generateAddToCartLoadingId(companyId, reportCode, 'buyNow')]) ||
    false
)

export const getOrderPageSize = createSelector(selectConfig, c => c.pageSizes['cart'] || 10)
